import { apply, burst, celebrate, toast } from '../core/effects';
import { bus } from '../core/bus';
import { S } from '../core/game';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { px, type MapId } from '../world/mapdef';

/**
 * Daily treasure hunt: three English riddles point at landmarks around the
 * world. A hot/cold compass helps, and you dig when you stand on the spot.
 */
export interface Spot {
  id: string;
  map: MapId;
  x: number;
  y: number;
}

/** Candidate hiding places; each one has an English riddle with the same id. */
export const SPOTS: readonly Spot[] = [
  { id: 'fountain', map: 'town', x: px(74) + 4, y: px(43) + 6 },
  { id: 'pond', map: 'town', x: px(37), y: px(36) },
  { id: 'cherry', map: 'town', x: px(38), y: px(11) + 4 },
  { id: 'busStop', map: 'town', x: px(35), y: px(23) - 4 },
  { id: 'slide', map: 'town', x: px(25) + 8, y: px(43) + 4 },
  { id: 'hay', map: 'town', x: px(74), y: px(56) + 6 },
  { id: 'tennis', map: 'town', x: px(60), y: px(18) },
  { id: 'track', map: 'town', x: px(3) + 8, y: px(19) + 4 },
  { id: 'mailbox', map: 'town', x: px(32) + 6, y: px(56) },
  { id: 'kiteTree', map: 'town', x: px(50), y: px(57) + 4 },
  { id: 'library', map: 'school', x: px(37), y: px(10) + 6 },
  { id: 'piano', map: 'school', x: px(49), y: px(21) + 4 },
  { id: 'easel', map: 'school', x: px(6), y: px(22) },
  { id: 'pool', map: 'sports', x: px(18) + 8, y: px(12) },
  { id: 'store', map: 'store', x: px(7), y: px(6) + 6 },
  { id: 'sofa', map: 'home', x: px(13) + 8, y: px(13) + 8 },
];

export const ALBUM = ['coin', 'marble', 'shell', 'robot', 'postcard', 'charm', 'compass', 'feather', 'stamp', 'crystal', 'whistle', 'medal'] as const;
export type AlbumItem = (typeof ALBUM)[number];
export const ALBUM_ICONS: Record<AlbumItem, string> = {
  coin: '🪙', marble: '🔮', shell: '🐚', robot: '🤖', postcard: '🏞️', charm: '🍀', compass: '🧭', feather: '🪶', stamp: '📮', crystal: '💎', whistle: '📯', medal: '🏅',
};

const DIG_RANGE = 14;

function dayNo(): number {
  const s = S();
  return (s.week - 1) * 3 + s.dayIndex;
}

/** Today's three spots (deterministic per day, different every day). */
export function todaysSpots(): Spot[] {
  const n = dayNo();
  const out: Spot[] = [];
  let k = n * 7 + 3;
  while (out.length < 3) {
    k = (k * 31 + 11) % 997;
    const sp = SPOTS[k % SPOTS.length];
    if (!out.includes(sp)) out.push(sp);
  }
  return out;
}

export function isFound(id: string): boolean {
  return !!S().daily[`treasure.${id}`];
}

/** Which clue the compass follows. */
let tracked: string | null = null;
export function trackClue(id: string | null): void {
  tracked = id;
  bus.emit('change');
}
export function trackedClue(): string | null {
  if (tracked && !isFound(tracked) && todaysSpots().some((s) => s.id === tracked)) return tracked;
  return todaysSpots().find((s) => !isFound(s.id))?.id ?? null;
}

/** Warmer/colder reading for the HUD, or null when there's nothing to find. */
export function compass(map: MapId, x: number, y: number): { text: string; level: number } | null {
  // The HUD compass only runs once you've picked a clue to follow.
  if (!tracked || isFound(tracked)) return null;
  const id = trackedClue();
  if (!id) return null;
  const sp = SPOTS.find((s) => s.id === id)!;
  const t = m().treasure;
  if (sp.map !== map) return { text: t.otherMap, level: 0 };
  const d = Math.hypot(sp.x - x, sp.y - y);
  const level = d < DIG_RANGE ? 4 : d < 48 ? 3 : d < 120 ? 2 : d < 260 ? 1 : 0;
  return { text: t.heat[level], level };
}

/** The spot you're standing on (for the dig action), if any. */
export function spotHere(map: MapId, x: number, y: number): Spot | null {
  return todaysSpots().find((s) => s.map === map && !isFound(s.id) && Math.hypot(s.x - x, s.y - y) < DIG_RANGE) ?? null;
}

export function dig(spot: Spot): void {
  const s = S();
  if (isFound(spot.id)) return;
  s.daily[`treasure.${spot.id}`] = true;
  sfx('rustle');
  burst('drop', 10);
  window.setTimeout(() => {
    sfx('fanfare');
    celebrate(true);
    // Each treasure gives coins and an album item you don't have yet (if any are left).
    const missing = ALBUM.filter((a) => !s.forever[`album.${a}`]);
    const item = missing.length ? missing[(dayNo() * 5 + spot.id.length) % missing.length] : null;
    if (item) s.forever[`album.${item}`] = true;
    const coins = 15 + ((spot.id.length * 7 + dayNo()) % 4) * 8;
    apply({ coins, stars: 4, mood: 8 });
    const t = m().treasure;
    toast(item ? t.foundItem(ALBUM_ICONS[item], t.album[item]) : t.found);
    if (todaysSpots().every((sp) => isFound(sp.id))) {
      window.setTimeout(() => {
        apply({ stars: 10, coins: 30 });
        toast(t.allFound);
      }, 900);
    }
    bus.emit('change');
  }, 500);
}

/** English riddles (kept in English on purpose: reading practice). */
export const RIDDLES: Record<string, string> = {
  fountain: 'Coins sleep under falling water. Look where people make a wish.',
  pond: 'Orange fish swim in circles. Search the grassy edge where they splash.',
  cherry: 'In spring this tree wears a pink dress. Dig under its petals.',
  busStop: 'People wait here for wheels and the number eight.',
  slide: 'Climb up the ladder, come down fast! Look next to it.',
  hay: 'Horses love this golden snack. Search beside the big pile.',
  tennis: 'Fifteen, thirty, forty, game! Look at the corner of the court.',
  track: 'Run in circles and never stop. Check the corner of the track.',
  mailbox: 'Letters and postcards wait inside a red box near home.',
  kiteTree: 'A kite once got stuck in this big tree in the neighbourhood.',
  library: 'Thousands of stories wait in silence. Look near the reading table.',
  piano: 'Black and white keys sing songs. Look beside them.',
  easel: 'Colours on paper and a palette nearby. Check the art room.',
  pool: 'Shower first, then splash! Look near the lifeguard’s chair.',
  store: 'Rice balls and tea eggs live here. Look between the shelves.',
  sofa: 'Where the family laughs at night, near the green cushions.',
};
